import { ChevronDown, ClipboardCheck, FileText, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getCurriculumsByPrimaryId,
  getScheduledProductsByProductCode,
  getCourseProductById,
  getAllProductCategories,
} from "../../api/action/products";
import { useCart } from "../../context/CartContext";
import { useAuthSession } from "../../auth/useAuthSession";
import { addToShoppingCart } from "./ProductToCart";
import { getReservationSlots } from "../../api/action/booking";
import { saveScheduledProduct } from "../../api/action/schedule";
import ScheduleCards from "./ScheduleCards";
import Loading from "../LandingPage/EducationDomain/Loading";
import RichTextDisplay from "../../components/RichTextDisplay";
import FormatByOperatingLocation from "../../components/FormatByOperatingLocation";
import { createOrder, updateOrder } from "../../api/action/cart";
import { setOrder } from "../../api/reducers/orderReducer";
import { useActiveEducationTheme } from "../../template/EducationThemeBridge.jsx";
import { Alert as UiAlert } from "../../components/ui/Feedback";
import { storePendingNavigation } from "../../utils/pendingNavigation.js";
import { getEffectiveDurationInMinutes } from "../../utils/durationUtils";
import { getBillingCycleLabel, isSubscriptionProduct } from "../../utils/subscriptionDisplay";
import {
  getEducationPageCacheKey,
  readEducationPageCache,
  writeEducationPageCache,
} from "../../domains/education/utils/educationPageCache";
import { KRUMAGO } from "../s3-assets/Krumago.jsx";

const ProdDetailsContext = createContext(null);
export const ProdDetailsProvider = ProdDetailsContext.Provider;
export const useCourseDetails = () => useContext(ProdDetailsContext);

const assetDisplayUrl = (asset, fallback = "") => {
  if (!asset) return fallback || "";
  if (typeof asset === "string") return asset;
  return asset.displayLink || asset.storedLink || fallback || "";
};

const normalizeApiList = (response) => {
  if (Array.isArray(response?.payload?.data)) return response.payload.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.payload)) return response.payload;
  if (Array.isArray(response)) return response;
  return [];
};

const getEntityId = (entity) => entity?.primaryId || entity?.id || entity?._id || null;

const getProductCategorySubType = (product) => {
  const subtype = product?.productCategorySubType
    || product?.product?.productCategorySubType
    || product?.scheduledProduct?.productCategorySubType
    || product?.scheduledProduct?.product?.productCategorySubType;

  if (typeof subtype === "string") return subtype.toUpperCase();
  return String(subtype?.name || subtype?.code || subtype?.typeCode || "").toUpperCase();
};

const getTodayInputValue = () => new Date().toISOString().slice(0, 10);

const toLocalDateTime = (date, time = "00:00:00") => {
  const normalizedTime = String(time || "00:00:00").slice(0, 8);
  return `${date}T${normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime}`;
};

const getSlotKey = (slot) => [
  slot?.assignedProductId,
  slot?.date,
  slot?.startTime,
  slot?.endTime
].filter(Boolean).join("-");

const entitySnapshot = (entity) => {
  try {
    return JSON.stringify(entity);
  } catch {
    return "";
  }
};

const getCategoryImageUrl = (category) => (
  category?.imageContent?.displayLink
    || category?.imageContent?.storedLink
    || category?.imageURL
    || ""
);

const normalizeProductImageUrl = (url) => {
  if (!url) return "";
  return /^https?:\/\//i.test(url)
    ? url
    : `https://managemybiz.s3.amazonaws.com/${url}`;
};

const getProductImageUrl = (product) => {
  const productRecord = product?.product || product;
  const wrapperImageUrl = product?.imageContents?.[0]?.displayLink
    || product?.imageContents?.[0]?.storedLink
    || product?.imageContent?.displayLink
    || product?.imageContent?.storedLink
    || product?.imageUrlsList?.[0]
    || product?.imageURL
    || "";
  const nestedImageUrl = productRecord?.imageContents?.[0]?.displayLink
    || productRecord?.imageContents?.[0]?.storedLink
    || productRecord?.imageContent?.displayLink
    || productRecord?.imageContent?.storedLink
    || productRecord?.imageUrlsList?.[0]
    || productRecord?.imageURL
    || "";

  return normalizeProductImageUrl(wrapperImageUrl || nestedImageUrl);
};

const isFeaturedCategory = (category) => Boolean(
  category?.hasFeatured
    || category?.featured
    || category?.isFeatured
);

const findCourseBannerCategory = (product, categories = []) => {
  const productCategory = product?.category || null;
  const productCategoryId = getEntityId(productCategory);
  const matchedCategory = categories.find((category) => (
    productCategoryId && String(getEntityId(category)) === String(productCategoryId)
  )) || productCategory;

  if (isFeaturedCategory(matchedCategory) && getCategoryImageUrl(matchedCategory)) {
    return matchedCategory;
  }

  return categories.find((category) => isFeaturedCategory(category) && getCategoryImageUrl(category))
    || (getCategoryImageUrl(matchedCategory) ? matchedCategory : null);
};

const hasTemplateCourseContent = (content = {}) => Boolean(
  content.title ||
  content.subtitle ||
  content.description ||
  content.imageUrl ||
  content.imageContent ||
  (Array.isArray(content.imageGallery) && content.imageGallery.length) ||
  (Array.isArray(content.scheduleOptions) && content.scheduleOptions.length) ||
  (Array.isArray(content.curriculum) && content.curriculum.length)
);

const formatCourseValidity = (days) => {
  const numericDays = Number(days);
  if (!numericDays || numericDays <= 0) {
    return { label: "Lifetime Access", detail: "No expiration — access never expires" };
  }
  let periodText;
  if (numericDays % 365 === 0) {
    const years = numericDays / 365;
    periodText = `${years} ${years === 1 ? "year" : "years"}`;
  } else if (numericDays % 30 === 0) {
    const months = numericDays / 30;
    periodText = `${months} ${months === 1 ? "month" : "months"}`;
  } else {
    periodText = `${numericDays} ${numericDays === 1 ? "day" : "days"}`;
  }
  return { label: `${periodText} access`, detail: `Access valid for ${periodText} after enrollment` };
};

const CourseInfo = ({ templateContent = {}, templateTheme = {} } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const activeEducationTheme = useActiveEducationTheme();
  const { isAuthenticated, isLoggedIn } = useAuthSession();
  const [searchParams] = useSearchParams();
  const hasTemplateContent = hasTemplateCourseContent(templateContent);
  const resolvedTemplateTheme = { ...activeEducationTheme, ...templateTheme };
  const templateAccent = resolvedTemplateTheme.primary || activeEducationTheme.primary;
  const templateSecondary = resolvedTemplateTheme.secondary || activeEducationTheme.secondary;
  const templateSurface = resolvedTemplateTheme.surface || activeEducationTheme.surface || "#ffffff";
  const templateText = resolvedTemplateTheme.color || activeEducationTheme.color || "#1f2937";
  const templateMuted = resolvedTemplateTheme.mutedColor || activeEducationTheme.mutedColor || "#6b7280";
  const templateBorder = resolvedTemplateTheme.borderColor || activeEducationTheme.borderColor || "#e5e7eb";
  const templateCardShadow = resolvedTemplateTheme.cardShadow || activeEducationTheme.cardShadow || "0 10px 30px rgba(15, 23, 42, 0.08)";
  const templatePageWidth = templateContent.maxWidth || resolvedTemplateTheme.coursePageWidth || "1600px";
  const templateGallery = Array.isArray(templateContent.imageGallery) && templateContent.imageGallery.length
    ? templateContent.imageGallery
    : [{ title: templateContent.title || "Course", imageUrl: templateContent.imageUrl, imageContent: templateContent.imageContent }];
  const templateMainImage = assetDisplayUrl(
    templateGallery[0]?.imageContent,
    templateGallery[0]?.imageUrl || templateContent.imageUrl || ""
  );
  const productPageStyle = {
    width: "100%",
    maxWidth: templatePageWidth,
    color: templateText,
    fontFamily: resolvedTemplateTheme.fontFamily,
    fontSize: "0.94rem",
    boxSizing: "border-box"
  };
  const productInfoBoxStyle = {
    width: "100%",
    maxWidth: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
    gap: "14px",
    alignItems: "stretch",
    background: templateSurface,
    border: `1px solid ${templateBorder}`,
    boxShadow: templateCardShadow,
    boxSizing: "border-box"
  };
  const productInfoItemClass = "flex min-w-0 flex-col items-start";
  const productInfoDividerClass = `${productInfoItemClass} border-b pb-4 md:border-b-0 md:border-r md:pb-0 md:pr-4`;
  const productInfoItemStyle = { borderColor: templateBorder, overflowWrap: "anywhere" };
  
  const { primaryId, isScheduledProduct } = useMemo(() => {
    const search = location.search;
    if (search.includes("?primaryId=")) {
      const match = search.match(/primaryId=(\d+)/);
      return {
        primaryId: match ? match[1] : "",
        isScheduledProduct: search.includes("scheduled-product")
      };
    }
    return {
      primaryId: searchParams.get("primaryId") || "",
      isScheduledProduct: searchParams.has("scheduled-product")
        || searchParams.get("scheduled-product") === "true"
        || search.includes("scheduled-product")
    };
  }, [location.search, searchParams]);
  
  const selectedProductFromStore = useSelector((state) => state.selectedProduct.selectedProduct);
  const selectedProductPrimaryId = selectedProductFromStore?.primaryId || selectedProductFromStore?.id;
  const [storedProduct, setStoredProduct] = useState(selectedProductFromStore);
  const [productLoading, setProductLoading] = useState(!selectedProductFromStore && !primaryId);
  
  const domain = localStorage.getItem("domainType");
  const [scheduledProduct, setScheduledProduct] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [curriculums, setCurriculums] = useState([]);
  const [curriculumError, setCurriculumError] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [interviewDate, setInterviewDate] = useState(getTodayInputValue());
  const [interviewSlots, setInterviewSlots] = useState([]);
  const [selectedInterviewSlotKey, setSelectedInterviewSlotKey] = useState("");
  const [interviewSlotsLoading, setInterviewSlotsLoading] = useState(false);
  const [interviewAvailabilityLoaded, setInterviewAvailabilityLoaded] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const { updateCart } = useCart();
  const companyState = useSelector((state) => state.companyReducer.company);
  const companyPrimaryId = companyState?.primaryId;
  const [expandAll, setExpandAll] = useState(false);
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [, setLoading] = useState({
    product: true,
    curriculum: true,
    scheduledProducts: true
  });

  const [mainImage, setMainImage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const setStableStoredProduct = useCallback((nextProduct) => {
    setStoredProduct((currentProduct) => (
      entitySnapshot(currentProduct) === entitySnapshot(nextProduct) ? currentProduct : nextProduct
    ));
  }, []);
  const setStableProductCategories = useCallback((nextCategories = []) => {
    setProductCategories((currentCategories) => {
      if (currentCategories.length !== nextCategories.length) return nextCategories;
      const changed = nextCategories.some((category, index) => (
        entitySnapshot(category) !== entitySnapshot(currentCategories[index])
      ));
      return changed ? nextCategories : currentCategories;
    });
  }, []);
  const setStableScheduledProduct = useCallback((nextScheduledProducts = []) => {
    setScheduledProduct((currentScheduledProducts) => (
      entitySnapshot(currentScheduledProducts) === entitySnapshot(nextScheduledProducts)
        ? currentScheduledProducts
        : nextScheduledProducts
    ));
  }, []);
  const setStableCurriculums = useCallback((nextCurriculums = []) => {
    setCurriculums((currentCurriculums) => (
      entitySnapshot(currentCurriculums) === entitySnapshot(nextCurriculums)
        ? currentCurriculums
        : nextCurriculums
    ));
  }, []);
  const setStableLoading = useCallback((nextLoading) => {
    setLoading((currentLoading) => {
      const resolvedLoading = typeof nextLoading === "function"
        ? nextLoading(currentLoading)
        : nextLoading;
      return entitySnapshot(currentLoading) === entitySnapshot(resolvedLoading)
        ? currentLoading
        : resolvedLoading;
    });
  }, []);

  // Determine if the product is upcoming
  const isUpcoming = storedProduct?.status === 'UPCOMING';

  useEffect(() => {
    let cancelled = false;
    if (primaryId) {
      if (!companyPrimaryId) {
        setProductLoading(true);
        return;
      }
      const fetchProduct = async () => {
        const cacheKey = getEducationPageCacheKey('course-info-product', {
          companyId: companyPrimaryId,
          primaryId,
          isScheduledProduct,
        });
        const cached = readEducationPageCache(cacheKey);
        if (cached) {
          setStableStoredProduct(cached);
          setProductLoading(false);
        }

        try {
          setProductLoading(!cached);
          
          const productResponse = await dispatch(
            getCourseProductById(companyPrimaryId, primaryId, isScheduledProduct)
          );
          if (cancelled) return;

          let productsArray = [];
          
          productsArray = normalizeApiList(productResponse);

          const foundProduct = productsArray.find(product => {
            if (!product) return false;
            const productId = product.primaryId || product.id;
            return productId === parseInt(primaryId) || productId?.toString() === primaryId;
          });

          if (foundProduct) {
            setStableStoredProduct(foundProduct);
            writeEducationPageCache(cacheKey, foundProduct);
          } else {
            if (cached) return;
            setAlertMsg({
              open: true,
              message: "Product not found. Please check the URL.",
              severity: "error",
            });
            setStableStoredProduct(null);
            setStableLoading({ product: false, curriculum: false, scheduledProducts: false });
          }
        } catch {
          if (cancelled) return;
          if (cached) return;
          setAlertMsg({
            open: true,
            message: "Failed to load product details.",
            severity: "error",
          });
          setStableStoredProduct(null);
          setStableLoading({ product: false, curriculum: false, scheduledProducts: false });
        } finally {
          if (!cancelled) setProductLoading(false);
        }
      };

      fetchProduct();
    } else if (selectedProductFromStore) {
      setStableStoredProduct(selectedProductFromStore);
      setProductLoading(false);

      // Catalog cards may carry a reduced product projection. Refresh it by ID
      // so subtype-specific detail flows never depend on the card payload.
      if (companyPrimaryId && selectedProductPrimaryId) {
        dispatch(getCourseProductById(companyPrimaryId, selectedProductPrimaryId, false))
          .then((productResponse) => {
            if (cancelled) return;
            const productsArray = normalizeApiList(productResponse);
            const freshProduct = productsArray.find((product) => (
              String(product?.primaryId || product?.id) === String(selectedProductPrimaryId)
            ));
            if (freshProduct) setStableStoredProduct(freshProduct);
          })
          .catch(() => undefined);
      }
    } else {
      setProductLoading(false);
      setStableStoredProduct(null);
      setStableLoading({ product: false, curriculum: false, scheduledProducts: false });
    }
    return () => { cancelled = true; };
  }, [primaryId, isScheduledProduct, companyPrimaryId, dispatch, selectedProductFromStore, selectedProductPrimaryId, setStableLoading, setStableStoredProduct]);

  useEffect(() => {
    if (!companyPrimaryId) {
      setStableProductCategories([]);
      return;
    }

    const cacheKey = getEducationPageCacheKey('course-info-categories', { companyId: companyPrimaryId });
    const cached = readEducationPageCache(cacheKey);
    if (cached) setStableProductCategories(cached);
    dispatch(getAllProductCategories(companyPrimaryId))
      .then((response) => {
        const categories = normalizeApiList(response);
        setStableProductCategories(categories);
        writeEducationPageCache(cacheKey, categories);
      })
      .catch(() => {
        if (!cached) setStableProductCategories([]);
      });
  }, [companyPrimaryId, dispatch, setStableProductCategories]);

  const courseBannerCategory = useMemo(
    () => findCourseBannerCategory(storedProduct, productCategories),
    [storedProduct, productCategories]
  );
  const courseBannerImage = useMemo(() => (
    storedProduct
      ? getProductImageUrl(storedProduct)
        || getCategoryImageUrl(courseBannerCategory)
        || KRUMAGO.IMAGES.LANDING_PAGE
      : templateMainImage
  ), [courseBannerCategory, storedProduct, templateMainImage]);

  const isInterviewProduct = useMemo(
    () => getProductCategorySubType(storedProduct) === "INTERVIEW",
    [storedProduct]
  );

  useEffect(() => {
    if (!storedProduct && courseBannerImage && mainImage !== courseBannerImage) {
      setMainImage(courseBannerImage);
    }
  }, [courseBannerImage, mainImage, storedProduct]);

  const isScheduleRequired = useMemo(() => {
    if (!storedProduct) return false;
    if (isInterviewProduct) return true;
    if (storedProduct.deliveryType === "BRICK_MORTAR") return true;
    if (storedProduct.deliveryType === "VIRTUAL" &&
      storedProduct.deliverySubType === "INSTRUCTOR_LED") return true;
    return false;
  }, [storedProduct, isInterviewProduct]);

  useEffect(() => {
    if (!storedProduct?.primaryId || productLoading) return;
    const cacheKey = getEducationPageCacheKey('course-info-details', {
      companyId: companyPrimaryId,
      productId: storedProduct.primaryId,
      domain,
    });
    const cached = readEducationPageCache(cacheKey);
    if (cached) {
      setStableScheduledProduct(cached.scheduledProduct || []);
      setStableCurriculums(cached.curriculums || []);
      setCurriculumError(false);
    }
    
    setStableLoading({
      product: false,
      curriculum: !cached,
      scheduledProducts: !cached && domain !== "RETAIL" && isScheduleRequired && !isInterviewProduct
    });

    if (domain !== "RETAIL" && isScheduleRequired && !isUpcoming && !isInterviewProduct) {
      dispatch(getScheduledProductsByProductCode(storedProduct.primaryId))
        .then((data) => {
          setStableScheduledProduct(data || []);
          writeEducationPageCache(cacheKey, {
            ...(readEducationPageCache(cacheKey) || {}),
            scheduledProduct: data || [],
          });
          setStableLoading(prev => ({ ...prev, scheduledProducts: false }));
        })
        .catch(() => {
          if (!cached) setStableScheduledProduct([]);
          setStableLoading(prev => ({ ...prev, scheduledProducts: false }));
        });
    } else {
      setStableLoading(prev => ({ ...prev, scheduledProducts: false }));
    }

    dispatch(getCurriculumsByPrimaryId(storedProduct.primaryId, ["EXAM"]))
      .then((data) => {
        setStableCurriculums(data || []);
        writeEducationPageCache(cacheKey, {
          ...(readEducationPageCache(cacheKey) || {}),
          curriculums: data || [],
        });
        setStableLoading(prev => ({ ...prev, curriculum: false }));
      })
      .catch(() => {
        if (!cached) {
          setCurriculumError(true);
          setStableCurriculums([]);
        }
        setStableLoading(prev => ({ ...prev, curriculum: false }));
      });
  }, [dispatch, storedProduct?.primaryId, domain, isScheduleRequired, isInterviewProduct, productLoading, isUpcoming, companyPrimaryId, setStableCurriculums, setStableLoading, setStableScheduledProduct]);

  const userDetail = useSelector((state) => state?.auth?.data) || {};
  const isUserLoggedIn = Boolean(
    (isAuthenticated || isLoggedIn) &&
    (userDetail?.primaryId || userDetail?.account?.primaryId || localStorage.getItem("token"))
  );
  const companyTimezone = companyState?.operatingLocation?.zoneId || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const selectedInterviewSlot = useMemo(() => (
    interviewSlots.find((slot) => getSlotKey(slot) === selectedInterviewSlotKey) || null
  ), [interviewSlots, selectedInterviewSlotKey]);

  useEffect(() => {
    if (!isInterviewProduct || !storedProduct?.primaryId || !companyPrimaryId || !interviewDate) {
      setInterviewSlots([]);
      setSelectedInterviewSlotKey("");
      setInterviewAvailabilityLoaded(false);
      return;
    }

    let cancelled = false;
    const fetchInterviewSlots = async () => {
      setInterviewSlotsLoading(true);
      setInterviewAvailabilityLoaded(false);
      setInterviewSlots([]);
      setSelectedInterviewSlotKey("");
      try {
        const companyWorkingSchedule = {
          ...companyState?.workingSchedule,
          workingDays: companyState?.workingSchedule?.workingDays || [],
          holidays: companyState?.workingSchedule?.holidays || [],
          openHours: companyState?.workingSchedule?.openHours || "08:00",
          closeHours: companyState?.workingSchedule?.closeHours || "18:00",
          slotIntervalInMinutes: companyState?.workingSchedule?.slotIntervalInMinutes || 60
        };
        const results = await dispatch(getReservationSlots({
          company: {
            primaryId: companyPrimaryId,
            domain: { type: "EDUCATION" },
            workingSchedule: companyWorkingSchedule
          },
          criteria: {
            durationType: "HOUR",
            product: {
              primaryId: storedProduct.primaryId,
              productCategorySubType: "INTERVIEW"
            },
            searchPeriod: {
              effectiveDate: {
                localStartDate: toLocalDateTime(interviewDate),
                timeZone: companyTimezone
              }
            }
          }
        }));

        if (cancelled) return;
        const matchingGroups = results?.matchingProductGroups
          || results?.data?.matchingProductGroups
          || results?.payload?.matchingProductGroups
          || [];
        const nextSlots = matchingGroups
          .flatMap((group) => Array.isArray(group?.slots)
            ? group.slots
            : Array.isArray(group?.matchingSlots) ? group.matchingSlots : [])
          .filter((slot) => slot?.date && slot?.startTime && slot?.endTime)
          .filter((slot, index, slots) => slots.findIndex((item) => getSlotKey(item) === getSlotKey(slot)) === index);
        setInterviewSlots(nextSlots);
      } catch (error) {
        if (cancelled) return;
        setInterviewSlots([]);
        setAlertMsg({
          open: true,
          message: error?.message || "Unable to load interview availability. Please try another date.",
          severity: "error",
        });
      } finally {
        if (!cancelled) {
          setInterviewSlotsLoading(false);
          setInterviewAvailabilityLoaded(true);
        }
      }
    };

    fetchInterviewSlots();
    return () => { cancelled = true; };
  }, [companyPrimaryId, companyState?.workingSchedule, companyTimezone, dispatch, interviewDate, isInterviewProduct, storedProduct?.primaryId]);

  const geOrderErrorMessage = useCallback((error) => {
    const rawMessage =
      error?.message ||
      error?.response?.data?.message ||
      error?.fallbackMessage ||
      (typeof error === "string" ? error : "");
    if (rawMessage.includes("Duplicate purchase is not allowed")) {
      return "You are already enrolled in this schedule. Please choose a different schedule or Course.";
    }
    if (rawMessage.includes("already purchased")) {
      return "This course has already been purchased for your account.";
    }
    return rawMessage || "We couldn't process your enrollment right now. Please try again.";
  }, []);

  const createOrderFromProduct = async (productDetails, isScheduled = false, selectedCourse = null) => {
      const lineItem = {
        quantity: 1,
        totalPrice: productDetails.price?.priceAmt || 0,
        price: {
          priceAmt: productDetails.price?.priceAmt || 0,
        }
      };

      if (isScheduled && selectedCourse) {
        lineItem.scheduledProduct = {
          product: {
            name: productDetails.name,
            code: productDetails.code,
            primaryId: productDetails.primaryId,
            type: productDetails.type,
            price: {
              priceAmt: productDetails.price?.priceAmt || 0,
              priceDurationType: productDetails.price?.priceDurationType,
            },
          },
          schedule: {
            primaryId: selectedCourse.primaryId,
          },
          primaryId: selectedCourse.primaryId,
        };
      } else {
        lineItem.product = {
          name: productDetails.name,
          code: productDetails.code,
          primaryId: productDetails.primaryId,
          type: productDetails.type,
          price: {
            priceAmt: productDetails.price?.priceAmt || 0,
            priceDurationType: productDetails.price?.priceDurationType,
          },
        };
      }

      const enrollmentCompany = (isScheduled && selectedCourse?.company) || productDetails.company;

      const orderRequest = {
        createdBy: `${userDetail.firstName} ${userDetail.lastName}`,
        modifiedBy: `${userDetail.firstName} ${userDetail.lastName}`,
        accountId: userDetail.account?.primaryId,
        totalLineItems: 1,
        totalAmount: productDetails.price?.priceAmt || 0,
        subtotalAmount: productDetails.price?.priceAmt || 0,
        shippingAndHandlingAmount: 0,
        tax: 0,
        deliveryType: storedProduct?.deliveryType || "BRICK_MORTAR",
        lineItemList: [lineItem],
        company: {
          primaryId: enrollmentCompany?.primaryId || companyState?.primaryId,
          domain: {
            type: enrollmentCompany?.domain?.type || companyState?.domain?.type,
          }
        }
      };

      const existingOrder = JSON.parse(localStorage.getItem("orderDetails"));
      let orderResponse;

      if (!existingOrder) {
        localStorage.removeItem('persistedOrder');
        orderResponse = await createOrder(orderRequest);
      } else {
        localStorage.removeItem('persistedOrder');
        orderResponse = await updateOrder({
          ...existingOrder,
          ...orderRequest
        });
      }

      localStorage.setItem("orderDetails", JSON.stringify(orderResponse));
      dispatch(setOrder(orderResponse));

      localStorage.setItem('persistedOrder', JSON.stringify({
        ...orderResponse,
        timestamp: new Date().getTime()
      }));

      return orderResponse;
  };

  const updateCartWithProduct = async (productDetails, isScheduled = false, selectedCourse = null) => {
      const shoppingCartId = userDetail?.account?.shoppingCart?.primaryId;

      const newItem = {
        quantity: 1,
        totalPrice: productDetails.price?.priceAmt || 0,
      };

      if (isScheduled && selectedCourse) {
        newItem.scheduledProduct = {
          primaryId: selectedCourse.primaryId,
          product: {
            ...productDetails,
            price: {
              priceAmt: productDetails.price?.priceAmt || 0,
              priceDurationType: productDetails.price?.priceDurationType,
            },
            period: selectedCourse.period,
            effectiveStartDate: selectedCourse.period?.effectiveStartDate,
            effectiveEndDate: selectedCourse.period?.effectiveEndDate,
          },
        };
      } else {
        newItem.product = {
          ...productDetails,
          primaryId: productDetails.primaryId,
          price: {
            priceAmt: productDetails.price?.priceAmt || 0,
            priceDurationType: productDetails.price?.priceDurationType,
          }
        };
      }

      const updatedProductList = [newItem];
      const totalAmount = updatedProductList.reduce((sum, item) => sum + item.totalPrice, 0);

      const cartPayload = {
        primaryId: shoppingCartId,
        accountId: userDetail.account.primaryId,
        createdBy: userDetail.firstName,
        modifiedBy: userDetail.firstName,
        totalAmount: totalAmount,
        totalPrice: totalAmount,
        productList: updatedProductList,
        company: companyState,
      };

      const cartResponse = await addToShoppingCart(cartPayload, navigate);
      if (cartResponse) {
        updateCart(updatedProductList);
        return cartResponse;
      }
  };

  const createInterviewScheduledProduct = async (productDetails, slot) => {
    const interviewerId = slot?.assignedProductId || slot?.suggestedProduct?.primaryId;
    if (!interviewerId) {
      throw new Error("The selected interviewer is no longer available. Please select another time.");
    }
    const durationMinutes = slot?.intervalInMinutes
      || Math.max(0, (new Date(toLocalDateTime(slot.date, slot.endTime)) - new Date(toLocalDateTime(slot.date, slot.startTime))) / 60000)
      || 60;
    return saveScheduledProduct({
      period: {
        effectiveDate: {
          localStartDate: toLocalDateTime(slot.date, slot.startTime),
          localEndDate: toLocalDateTime(slot.date, slot.endTime),
          timeZone: companyTimezone,
          considerTimePortion: true
        },
        durationType: "HOUR",
        duration: durationMinutes / 60
      },
      owners: [{ primaryId: interviewerId }],
      clients: userDetail?.primaryId ? [{ primaryId: userDetail.primaryId }] : [],
      product: {
        primaryId: productDetails.primaryId,
        productCategorySubType: "INTERVIEW"
      },
      company: { primaryId: companyPrimaryId },
      createdBy: `${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim() || userDetail.email || "user",
      modifiedBy: `${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim() || userDetail.email || "user",
      hasSchedule: true,
      status: "ACTIVE",
      hasChild: false,
      parentProductId: null,
      associatedScheduledProducts: [],
      name: `${productDetails.name} - ${slot.suggestedProduct?.name || "Interview"} - ${slot.date}`
    });
  };

  const handleCart = async (productDetails) => {
    if (!isUserLoggedIn) {
      storePendingNavigation();
      setAlertMsg({
        message: "Please sign in to continue with enrollment.",
        open: true,
        severity: "warning",
      });
      setTimeout(() => {
        navigate("/sign-in");
      }, 1200);
      return;
    }

    if (isInterviewProduct) {
      if (!selectedInterviewSlot) {
        setAlertMsg({
          message: "Please select an interviewer and time slot.",
          open: true,
          severity: "warning",
        });
        return;
      }
      try {
        setIsProcessing(true);
        const createdSchedule = await createInterviewScheduledProduct(productDetails, selectedInterviewSlot);
        if (!createdSchedule?.primaryId) {
          throw new Error("Interview schedule could not be created. Please select another time.");
        }
        await updateCartWithProduct(productDetails, true, createdSchedule);
        const orderResponse = await createOrderFromProduct(productDetails, true, createdSchedule);
        if (orderResponse) navigate("/enrollment-details");
      } catch (err) {
        setAlertMsg({
          message: geOrderErrorMessage(err).includes("slot")
            ? "This slot is no longer available. Please select another time."
            : geOrderErrorMessage(err),
          open: true,
          severity: "error",
        });
        setIsProcessing(false);
      }
    } else if (!isScheduleRequired) {
      try {
        setIsProcessing(true);
        await updateCartWithProduct(productDetails, false, null);
        const orderResponse = await createOrderFromProduct(productDetails, false, null);
        if (orderResponse) navigate("/enrollment-details");
      } catch (err) {
        setAlertMsg({
          message: geOrderErrorMessage(err),
          open: true,
          severity: "error",
        });
        setIsProcessing(false);
      }
    } else if (selectedSchedule) {
      try {
        setIsProcessing(true);
        const selectedCourse = JSON.parse(selectedSchedule);
        await updateCartWithProduct(productDetails, true, selectedCourse);
        const orderResponse = await createOrderFromProduct(productDetails, true, selectedCourse);
        if (orderResponse) navigate("/enrollment-details");
      } catch (err) {
        setAlertMsg({
          message: geOrderErrorMessage(err),
          open: true,
          severity: "error",
        });
        setIsProcessing(false);
      }
    } else {
      setAlertMsg({
        message: "Please pick your schedule.",
        open: true,
        severity: "warning",
      });
    }
  };

  const handleClose = (...closeArgs) => {
    const reason = closeArgs[1];
    if (reason === "clickaway") return;
    setAlertMsg({ ...alertMsg, open: false });
  };

  useEffect(() => {
    if (!alertMsg.open) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAlertMsg((current) => ({ ...current, open: false }));
    }, 6000);

    return () => window.clearTimeout(timeoutId);
  }, [alertMsg.open, alertMsg.message, alertMsg.severity]);

  const toggleExpansion = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleExpandAll = () => {
    const newExpandedItems = {};
    const allExpanded = !expandAll;
    curriculums.forEach((curriculum) => {
      curriculum.chapterList?.forEach((chapter) => {
        newExpandedItems[chapter.primaryId] = allExpanded;
      });
      curriculum.examsList?.forEach((exam) => {
        newExpandedItems[exam.primaryId] = allExpanded;
      });
    });
    setExpandedItems(newExpandedItems);
    setExpandAll(allExpanded);
  };

  const handleScheduleChange = (event) => {
    setSelectedSchedule(event.target.value);
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatInterviewTime = (time) => {
    if (!time) return "";
    const [hours = "0", minutes = "0"] = String(time).split(":");
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit"
    }).format(new Date(2020, 0, 1, Number(hours), Number(minutes)));
  };

  const renderInterviewScheduleSection = () => {
    const slotsByInterviewer = interviewSlots.reduce((groups, slot) => {
      const interviewerId = slot.assignedProductId || "unknown";
      if (!groups[interviewerId]) {
        groups[interviewerId] = {
          interviewer: slot.suggestedProduct || { primaryId: interviewerId, name: "Interviewer" },
          slots: []
        };
      }
      groups[interviewerId].slots.push(slot);
      return groups;
    }, {});
    const interviewerGroups = Object.values(slotsByInterviewer);

    return (
      <>
        <div id="course" className="mb-5 mt-14 items-left justify-left overflow-hidden md:mb-6 lg:mb-5 xl:flex">
          <h3 className="text-2xl font-semibold" id="schedule-title" style={{ color: templateAccent }}>
            {templateContent.scheduleTitle || "Choose Your Interview Time"}
          </h3>
        </div>
        <div id="schedule" className="mt-8 rounded-lg border p-5" style={{ background: templateSurface, borderColor: templateBorder }}>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <label className="flex flex-col gap-2 text-sm font-semibold" style={{ color: templateSecondary }}>
              Interview date
              <input
                type="date"
                min={getTodayInputValue()}
                value={interviewDate}
                onChange={(event) => setInterviewDate(event.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
              />
            </label>
            {selectedInterviewSlot && (
              <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                {selectedInterviewSlot.suggestedProduct?.name || "Interviewer"} - {selectedInterviewSlot.date} - {formatInterviewTime(selectedInterviewSlot.startTime)} - {formatInterviewTime(selectedInterviewSlot.endTime)}
              </div>
            )}
          </div>

          {interviewSlotsLoading || !interviewAvailabilityLoaded ? (
            <div className="mt-6 flex items-center gap-3 rounded-md border px-4 py-4 text-sm" style={{ color: templateSecondary, borderColor: templateBorder, background: templateSurface }} role="status" aria-live="polite">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
              <span>Getting interviewer availability...</span>
            </div>
          ) : interviewerGroups.length === 0 ? (
            <p className="mt-6 text-sm" style={{ color: templateMuted }}>No interview slots are available for this date.</p>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
              {interviewerGroups.map(({ interviewer, slots }) => (
                <div key={interviewer.primaryId || interviewer.name} className="rounded-lg border p-4" style={{ borderColor: templateBorder }}>
                  <h4 className="text-lg font-bold" style={{ color: templateSecondary }}>{interviewer.name || "Interviewer"}</h4>
                  {interviewer.description && <p className="mt-1 text-sm" style={{ color: templateMuted }}>{interviewer.description}</p>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {slots.map((slot) => {
                      const slotKey = getSlotKey(slot);
                      const isOpen = String(slot.status?.name || slot.status || "").toUpperCase() === "OPEN";
                      const isSelected = selectedInterviewSlotKey === slotKey;
                      return (
                        <button
                          key={slotKey}
                          type="button"
                          disabled={!isOpen}
                          onClick={() => setSelectedInterviewSlotKey(slotKey)}
                          className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                            isSelected
                              ? "border-emerald-600 bg-emerald-600 text-white"
                              : isOpen
                                ? "border-gray-300 bg-white text-gray-800 hover:border-emerald-500"
                                : "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          }`}
                        >
                          {formatInterviewTime(slot.startTime)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderTemplateCoursePreview = () => {
    const scheduleOptions = Array.isArray(templateContent.scheduleOptions) ? templateContent.scheduleOptions : [];
    const curriculumItems = Array.isArray(templateContent.curriculum) ? templateContent.curriculum : [];
    const chapterCount = curriculumItems.filter((item) => String(item?.type || "chapter").toLowerCase() !== "exam").length;
    const examCount = curriculumItems.filter((item) => String(item?.type || "").toLowerCase() === "exam").length;
    const curriculumTitle = templateContent.curriculumTitleTemplate
      || `${templateContent.title || "Course"} has ${chapterCount} chapters and ${examCount} exams`;

    return (
      <div className="sp mx-auto w-full px-2 py-8 lg:px-0" id="product-details-page" style={productPageStyle}>
        <div className="mb-7 w-full max-w-full pt-4 md:px-6 md:pt-6 lg:mb-8 lg:p-6 2xl:p-8 2xl:pt-8" id="product-wrapper">
          <div className="flex w-full min-w-0 flex-col items-start justify-between gap-6 lg:flex-row lg:space-x-0" id="product-main-section">
            <div className="relative flex h-full min-w-0 flex-1 items-start justify-center rounded-lg bg-slate-50 p-4" id="main-product-image-container">
              {templateMainImage && (
                <img
                  loading="lazy"
                  id="main-product-image"
                  src={mainImage || templateMainImage}
                  alt={templateContent.title || "Course"}
                  width={320}
                  height={240}
                  className="max-h-[340px] w-full max-w-[520px] rounded-lg object-contain"
                />
              )}
            </div>

            <div className="mb-4 mt-4 flex flex-wrap justify-center lg:mt-0 lg:flex-col" id="product-thumbnails">
              {templateGallery.map((item, index) => {
                const imageUrl = assetDisplayUrl(item.imageContent, item.imageUrl || templateMainImage);
                if (!imageUrl) return null;
                return (
                  <img
                    loading="lazy"
                    key={`${item.title || "thumb"}-${index}`}
                    id={`product-thumbnail-${index}`}
                    src={imageUrl}
                    alt={item.title || `Course thumbnail ${index + 1}`}
                    className="m-1 h-16 w-16 rounded-md border border-gray-300 object-cover shadow-sm"
                  />
                );
              })}
            </div>
          </div>

          <div className="mb-4 flex items-center justify-start md:mb-6 lg:mb-5 xl:flex" style={{ marginTop: "24px" }} id="enroll-section">
            <button
              type="button"
              id="enroll-now-button"
              className="flex h-16 w-60 items-center justify-center rounded-lg text-lg font-semibold text-white shadow-lg"
              style={{ backgroundColor: templateContent.buttonColor || templateSecondary }}
            >
              {templateContent.enrollLabel || "Enroll Now"} <span className="ml-2 text-3xl">-&gt;</span>
            </button>
          </div>

          <div className="mb-4 flex flex-col items-start justify-start overflow-hidden md:mb-6 lg:mb-5" id="product-name-section">
            <h3 className="text-2xl font-semibold" style={{ color: templateAccent }} id="product-name">
              {templateContent.title || "Course Detail"}
            </h3>
            {templateContent.subtitle && <p className="mt-2 max-w-3xl text-base" style={{ color: templateMuted }}>{templateContent.subtitle}</p>}
          </div>

          <div className="overflow-hidden" id="product-info-section">
            <div className="rounded-lg px-4 py-6" id="product-info-box" style={productInfoBoxStyle}>
              <div className={productInfoDividerClass} id="delivery-type-section" style={productInfoItemStyle}>
                <h3 className="text-lg font-semibold" style={{ color: templateSecondary }} id="delivery-type-title">{templateContent.deliveryTitle || "Delivery"}</h3>
                <p className="text-sm" style={{ color: templateMuted }} id="delivery-type-description">{templateContent.deliveryDescription || "Course delivery information"}</p>
              </div>

              <div className={productInfoDividerClass} id="category-section" style={productInfoItemStyle}>
                <h3 className="text-lg font-semibold" style={{ color: templateSecondary }} id="category-name">{templateContent.categoryTitle || "Category"}</h3>
                <p className="text-sm" style={{ color: templateMuted }} id="category-description">{templateContent.categoryDescription || "Course category information"}</p>
              </div>

              <div className={productInfoDividerClass} id="schedule-link-section" style={productInfoItemStyle}>
                <h3 className="text-lg font-semibold" style={{ color: templateSecondary }}>{templateContent.scheduleLinkTitle || "Flexible schedule"}</h3>
                <p className="text-sm" style={{ color: templateMuted }}>
                  <a href="#course" onClick={(event) => event.preventDefault()} className="hover:underline" style={{ color: templateAccent }} id="pick-schedule-link">
                    {templateContent.scheduleLinkLabel || "Pick your schedule"}
                  </a>
                </p>
              </div>

              <div className={productInfoDividerClass} id="validity-section" style={productInfoItemStyle}>
                <h3 className="text-lg font-semibold" style={{ color: templateSecondary }} id="validity-title">{templateContent.validityTitle || "Course Validity"}</h3>
                <p className="text-sm" style={{ color: templateMuted }} id="validity-description">{templateContent.validityDescription || "Lifetime access — no expiration"}</p>
              </div>

              <div className={productInfoItemClass} id="about-course-section" style={{ overflowWrap: "anywhere" }}>
                <h3 className="text-lg font-semibold" style={{ color: templateSecondary }}>{templateContent.aboutTitle || "About the course"}</h3>
                <p className="text-sm" style={{ color: templateMuted }}>
                  <a href="#description" onClick={(event) => event.preventDefault()} className="hover:underline" style={{ color: templateAccent }} id="learn-more-link">
                    {templateContent.aboutLinkLabel || "Learn more"}
                  </a>
                </p>
              </div>
            </div>
          </div>

          {!!scheduleOptions.length && (
            <>
              <div id="course" className="mb-5 mt-14 items-left justify-left overflow-hidden md:mb-6 lg:mb-5 xl:flex">
                <h3 className="text-2xl font-semibold" style={{ color: templateAccent }} id="schedule-title">{templateContent.scheduleTitle || "Explore Your Schedule Options"}</h3>
              </div>

              <div id="schedule" className="mt-8 w-full max-w-9xl rounded-lg p-5">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {scheduleOptions.map((item, index) => (
                    <article key={`${item.title || "schedule"}-${index}`} className="rounded-lg border p-5 shadow-md" style={{ background: templateSurface, borderColor: templateBorder }}>
                      <h4 className="text-lg font-bold" style={{ color: templateSecondary }}>{item.title}</h4>
                      <p className="mt-2 text-sm" style={{ color: templateMuted }}>{item.description}</p>
                      <div className="mt-4 flex flex-wrap gap-2 text-sm">
                        {item.date && <span className="rounded-md bg-blue-50 px-3 py-1 font-semibold text-blue-700">{item.date}</span>}
                        {item.time && <span className="rounded-md bg-teal-50 px-3 py-1 font-semibold text-teal-700">{item.time}</span>}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </>
          )}

          <div id="description" className="mt-10 p-5">
            <h3 className="text-2xl font-semibold" style={{ color: templateAccent }} id="description-title">{templateContent.descriptionTitle || "About the course"}</h3>
            <div className="mt-2 text-justify text-base" id="description-text" style={{ color: templateMuted }}>
              {templateContent.description ? (
                <RichTextDisplay content={templateContent.description} />
              ) : (
                <p className="text-gray-500 italic">No description available for this course.</p>
              )}
            </div>
          </div>

          {!!curriculumItems.length && (
            <div className="pt-6 xl:pt-8" style={{ marginTop: "56px" }} id="curriculum-section">
              <div className="p-4 md:p-6 lg:p-8" id="curriculum-preview">
                <div className="flex flex-col items-center justify-between md:flex-row">
                  <h4 className="text-xl font-semibold md:text-2xl" style={{ color: templateAccent }}>
                    {curriculumTitle}
                  </h4>
                  <span className="mt-2 cursor-pointer font-bold underline md:mt-0" style={{ color: templateSecondary }}>Expand All</span>
                </div>

                {curriculumItems.map((item, index) => {
                  const isExam = String(item?.type || "").toLowerCase() === "exam";
                  return (
                    <details key={`${item.title || "curriculum"}-${index}`} className="mb-4 rounded-lg border shadow-md" id={`curriculum-item-${index}`} style={{ background: templateSurface, borderColor: templateBorder }}>
                      <summary className="flex cursor-pointer list-none items-center justify-between p-4 font-semibold">
                        <span><strong>{isExam ? "Exam" : `Chapter ${index + 1}`}:</strong> {item.title}</span>
                        <span style={{ color: templateSecondary }}>Expand</span>
                      </summary>
                      <div className="border-t border-gray-100 p-4 text-sm text-gray-600">
                        {item.description && <RichTextDisplay content={item.description} />}
                        {Array.isArray(item.contents) && !!item.contents.length && (
                          <ul className="mt-3 list-disc pl-5">
                            {item.contents.map((lesson) => <li key={lesson}>{lesson}</li>)}
                          </ul>
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-8 mt-12 text-center">
            <button type="button" className="font-bold underline" style={{ color: templateSecondary }}>Scroll to Top</button>
          </div>
        </div>
      </div>
    );
  };

  // Helper to render the enroll/upcoming button
  const renderEnrollButton = () => {
    if (isUpcoming) {
      return (
        <div className="flex flex-col items-start space-y-2">
          <button
            type="button"
            disabled={true}
            className="h-16 w-60 rounded-lg text-lg font-semibold bg-gray-300 text-gray-600 cursor-not-allowed shadow-md flex items-center justify-center"
          >
            Available Soon
          </button>
          <p className="text-sm text-blue-600 italic">
            📘 This course is coming soon. Stay tuned for enrollment!
          </p>
        </div>
      );
    }

    return (
      <button
        type="button"
        id="enroll-now-button"
        onClick={() => handleCart(storedProduct)}
        disabled={isProcessing || (isInterviewProduct && !selectedInterviewSlot)}
        className={`h-16 w-60 rounded-lg text-lg font-semibold text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition duration-300 ease-in-out flex items-center justify-center ${
          isProcessing
            ? 'cursor-not-allowed opacity-90'
            : 'transform hover:scale-105 bg-blue-700 hover:bg-blue-800 focus:ring-blue-500'
        }`}
        style={{ backgroundColor: templateContent.buttonColor || templateSecondary, borderRadius: resolvedTemplateTheme.buttonRadius || "8px" }}
      >
        {isProcessing ? (
          <>
            <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {templateContent.processingLabel || "Processing..."}
          </>
        ) : (
          <>
            {isInterviewProduct
              ? (templateContent.interviewEnrollLabel || "Book Interview")
              : (templateContent.enrollLabel || "Enroll Now")}
            <span className="ml-2 text-3xl">&#10148;</span>
          </>
        )}
      </button>
    );
  };

  // Fixed renderScheduleSection to show schedules for normal courses
  const renderScheduleSection = () => {
    if (isUpcoming) {
      return (
        <div className="mt-8 p-6 rounded-2xl w-full max-w-9xl bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-200 text-center shadow-sm">
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <div className="p-4 rounded-full bg-white shadow-md">
              <svg className="w-14 h-14 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">Something Exciting is Coming!</h3>
            <p className="text-gray-600 max-w-xl leading-relaxed">
              We're crafting this course with care to deliver an engaging and high-quality learning journey just for you.
              Stay tuned — launch day is closer than you think!
            </p>
            <div className="px-5 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
              🚀 Launching Soon
            </div>
          </div>
        </div>
      );
    }

    if (isInterviewProduct) {
      return renderInterviewScheduleSection();
    }

    // For normal courses that require schedule selection
    if (isScheduleRequired) {
      return (
        <>
          <div id="course" className="mb-5 mt-14 items-left justify-left overflow-hidden md:mb-6 lg:mb-5 xl:flex">
            <h3 className="text-2xl font-semibold" id="schedule-title" style={{ color: templateAccent }}>{templateContent.scheduleTitle || "Explore Your Schedule Options"}</h3>
          </div>
          <div id="schedule" className="mt-8 p-5 rounded-lg w-full max-w-9xl">
            <ProdDetailsProvider value={{ handleScheduleChange, selectedSchedule }}>
              <ScheduleCards scheduleData={scheduledProduct} />
            </ProdDetailsProvider>
          </div>
        </>
      );
    }

    return null;
  };

  if (!primaryId && !selectedProductFromStore && !storedProduct && hasTemplateContent && !productLoading) {
    return renderTemplateCoursePreview();
  }

  // Product data is enough to render the page. Curriculum and schedules refresh
  // independently so a slow secondary request cannot block the course view.
  if (productLoading) {
    return (
      <Loading
        message="Getting your course ready..."
        subMessage="Just a sec! We're setting up the details for you."
        open={true}
      />
    );
  }

  if (!storedProduct) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <UiAlert tone="error" className="mb-4">
          Product not found. Please go back and try again.
        </UiAlert>
      </div>
    );
  }

  const getDeliveryTypeLabels = () => {
    if (isInterviewProduct) {
      return {
        title: "Interview",
        description: "Pick an interviewer and available time"
      };
    }
    const deliveryType = storedProduct?.deliveryType;
    if (deliveryType === "BRICK_MORTAR") {
      return {
        title: "In-Classroom Experience",
        description: "Recommended experience"
      };
    } else if (deliveryType === "VIRTUAL") {
      if (storedProduct?.deliverySubType === "INSTRUCTOR_LED") {
        return {
          title: "Instructor-led Online",
          description: "Live virtual sessions with instructor"
        };
      } else {
        return {
          title: "Self-paced Online",
          description: "Learn at your own pace"
        };
      }
    } else if (deliveryType === "DIGITAL") {
      return {
        title: "Online",
        description: "Join online sessions"
      };
    } else {
      return {
        title: deliveryType || "Delivery Type",
        description: "Recommended experience"
      };
    }
  };

  const deliveryLabels = getDeliveryTypeLabels();
  const courseValidity = formatCourseValidity(storedProduct?.lifeTimeInDays);
  const isSubscriptionCourse = isSubscriptionProduct(storedProduct);
  const subscriptionBillingCycleLabel = getBillingCycleLabel(storedProduct?.price?.priceDurationType || storedProduct?.priceDurationType);
  const subscriptionPrice = storedProduct?.price?.priceAmt || 0;

  return (
    <div className="sp mx-auto w-full px-2 py-8 lg:px-0" id="product-details-page" style={productPageStyle}>
      <div className="mb-7 w-full max-w-full pt-4 md:px-6 md:pt-6 lg:mb-8 lg:p-6 2xl:p-8 2xl:pt-8" id="product-wrapper">
        <div className="flex w-full min-w-0 flex-col items-start justify-between gap-6 lg:flex-row lg:space-x-0" id="product-main-section">
          <div className="relative flex h-full min-w-0 flex-1 items-start justify-center rounded-lg bg-slate-50 p-4" id="main-product-image-container">
            <img
              loading="lazy"
              id="main-product-image"
              src={courseBannerImage}
              alt={storedProduct?.name || "Product"}
              width={320}
              height={240}
              className="max-h-[340px] w-full max-w-[520px] cursor-pointer rounded-lg object-contain"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = KRUMAGO.IMAGES.LANDING_PAGE;
              }}
            />
          </div>
        </div>

        {/* Enrollment Button Section */}
        <div className="flex items-center justify-start mb-4 md:mb-6 lg:mb-5 xl:flex" style={{ marginTop: "24px" }} id="enroll-section">
          {renderEnrollButton()}
        </div>

        <div className="mb-4 flex flex-col items-start justify-start overflow-hidden md:mb-6 lg:mb-5" id="product-name-section">
          <h3 className="text-2xl font-semibold" id="product-name" style={{ color: templateAccent }}>
            {storedProduct?.name || "Product Name"}
          </h3>
        </div>

        <div className="w-full overflow-hidden" id="product-info-section">
          <div className="rounded-lg px-4 py-6" id="product-info-box" style={productInfoBoxStyle}>
            <div className={productInfoDividerClass} id="delivery-type-section" style={productInfoItemStyle}>
              <h3 className="text-lg font-semibold" id="delivery-type-title" style={{ color: templateSecondary }}>
                {deliveryLabels.title}
              </h3>
              <p className="text-sm" style={{ color: templateMuted }} id="delivery-type-description">
                {deliveryLabels.description}
              </p>
            </div>

            <div className={productInfoDividerClass} id="category-section" style={productInfoItemStyle}>
              <h3 className="text-lg font-semibold" id="category-name" style={{ color: templateSecondary }}>
                {storedProduct?.category?.name || "Category"}
              </h3>
              <p className="text-sm" style={{ color: templateMuted }} id="category-description">
                Discover exciting {storedProduct?.category?.name || "Category"} experiences!
              </p>
            </div>

            {!isUpcoming && isScheduleRequired && !isInterviewProduct && (
              <div className={productInfoDividerClass} id="schedule-link-section" style={productInfoItemStyle}>
                <h3 className="text-lg font-semibold" style={{ color: templateSecondary }}>{templateContent.scheduleLinkTitle || "Flexible schedule"}</h3>
                <p className="text-sm" style={{ color: templateMuted }}>
                  <button
                    type="button"
                    className="hover:underline"
                    style={{ color: templateAccent, background: "transparent", border: 0, padding: 0 }}
                    id="pick-schedule-link"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById("course")?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {templateContent.scheduleLinkLabel || "Pick your schedule"}
                  </button>
                </p>
              </div>
            )}

            <div className={productInfoDividerClass} id="validity-section" style={productInfoItemStyle}>
              <h3 className="text-lg font-semibold" id="validity-title" style={{ color: templateSecondary }}>
                {isSubscriptionCourse ? "Subscription Fee" : courseValidity.label}
              </h3>
              <p className="text-sm" style={{ color: templateMuted }} id="validity-description">
                {isSubscriptionCourse ? (
                  <>
                    <FormatByOperatingLocation value={subscriptionPrice} type="CURRENCY" />
                    {subscriptionBillingCycleLabel ? ` / ${subscriptionBillingCycleLabel}` : ""}
                  </>
                ) : courseValidity.detail}
              </p>
            </div>

            <div className={productInfoItemClass} id="about-course-section" style={{ overflowWrap: "anywhere" }}>
              <h3 className="text-lg font-semibold" style={{ color: templateSecondary }}>{templateContent.aboutTitle || "About the course"}</h3>
              <p className="text-sm" style={{ color: templateMuted }}>
                <button
                  type="button"
                  className="hover:underline"
                  style={{ color: templateAccent, background: "transparent", border: 0, padding: 0 }}
                  id="learn-more-link"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("description")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {templateContent.aboutLinkLabel || "Learn more"}
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Schedule Section - fixed to show schedules for normal courses */}
        {renderScheduleSection()}

        <div id="description" className="mt-10 p-5">
          <h3 className="text-2xl font-semibold" id="description-title" style={{ color: templateAccent }}>{templateContent.descriptionTitle || "About the course"}</h3>
          <div className="text-base mt-2 text-justify" id="description-text" style={{ color: templateMuted }}>
            {storedProduct?.description ? (
              <RichTextDisplay 
                content={storedProduct.description}
                sx={{
                  fontSize: '16px',
                  lineHeight: 1.6,
                  fontFamily: 'Arial, sans-serif',
                  color: '#374151',
                  '& h1': {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    margin: '16px 0 8px 0',
                    color: '#1f2937'
                  },
                  '& h2': {
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    margin: '14px 0 7px 0',
                    color: '#374151'
                  },
                  '& h3': {
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    margin: '12px 0 6px 0',
                    color: '#4b5563'
                  },
                  '& p': {
                    margin: '12px 0',
                    color: '#4b5563'
                  },
                  '& ul, & ol': {
                    margin: '12px 0',
                    paddingLeft: '24px',
                    color: '#4b5563'
                  },
                  '& li': {
                    margin: '6px 0'
                  },
                  '& strong, & b': {
                    fontWeight: 'bold',
                    color: '#1f2937'
                  },
                  '& em, & i': {
                    fontStyle: 'italic'
                  },
                  '& a': {
                    color: templateAccent,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: '#2d746e'
                    }
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    margin: '16px 0'
                  },
                  '& blockquote': {
                    borderLeft: `4px solid ${templateAccent}`,
                    margin: '16px 0',
                    paddingLeft: '16px',
                    fontStyle: 'italic',
                    color: '#6b7280'
                  }
                }}
              />
            ) : (
              <p className="text-gray-500 italic">
                No description available for this course.
              </p>
            )}
          </div>
        </div>

        {!curriculumError && curriculums?.length > 0 && (
          <div className="pt-6 xl:pt-8" style={{ marginTop: "56px" }} id="curriculum-section">
            {curriculums.map((curriculum) => {
              if (!curriculum) return null;

              const filteredExamsList = curriculum.examsList?.filter(exam => exam.subType !== "Quiz") || [];
              const chapterCount = curriculum.chapterList?.length || 0;
              const examCount = filteredExamsList.length;
              const totalCourseMinutes = [
                ...(curriculum.chapterList || []),
                ...filteredExamsList,
              ].reduce((sum, entry) => {
                if (entry?.contentList?.length) {
                  return sum + entry.contentList.reduce((contentSum, content) => contentSum + getEffectiveDurationInMinutes(content), 0);
                }
                return sum + ((Number(entry?.durationInHours ?? entry?.duration) || 0) * 60);
              }, 0);
              const totalCourseHours = totalCourseMinutes / 60;

              const combinedList = [
                ...(curriculum.chapterList || []).map((ch, i) => ({ ...ch, type: "C", chapterIndex: i + 1 })),
                ...filteredExamsList.map((ex, i) => ({ ...ex, type: "E", examIndex: i + 1 })),
              ].sort((a, b) => (a.curriculumSerialNumber || 0) - (b.curriculumSerialNumber || 0));

              return (
                <div key={curriculum._id || curriculum.primaryId} className="p-4 md:p-6 lg:p-8" id={`curriculum-${curriculum._id}`}>
                  <div className="flex flex-col md:flex-row items-center justify-between">
                    <h4 className="text-xl md:text-2xl font-semibold" style={{ color: templateAccent }}>
                      {storedProduct?.name || "Product Name"}
                      {chapterCount > 0 && examCount > 0 && (
                        <> has {chapterCount} {chapterCount === 1 ? "chapter" : "chapters"} and {examCount} {examCount === 1 ? "exam" : "exams"}</>
                      )}
                      {chapterCount > 0 && examCount === 0 && (
                        <> has {chapterCount} {chapterCount === 1 ? "chapter" : "chapters"}</>
                      )}
                      {chapterCount === 0 && examCount > 0 && (
                        <> has {examCount} {examCount === 1 ? "exam" : "exams"}</>
                      )}
                      {chapterCount === 0 && examCount === 0 && (
                        <> has no curriculum content available</>
                      )}
                    </h4>
                    <span
                      className="cursor-pointer underline font-bold mt-2 md:mt-0"
                      style={{ color: templateSecondary }}
                      id={`expand-collapse-${curriculum._id}`}
                      onClick={handleExpandAll}
                    >
                      {expandAll ? "Collapse All" : "Expand All"}
                    </span>
                  </div>

                  {totalCourseHours > 0 && (
                    <p
                      className="mt-1 text-sm font-semibold"
                      style={{ color: templateSecondary }}
                      id={`curriculum-total-hours-${curriculum._id}`}
                    >
                      Total course hours: {Number.isInteger(totalCourseHours) ? totalCourseHours : Number(totalCourseHours.toFixed(1))} {totalCourseHours === 1 ? "hour" : "hours"}
                    </p>
                  )}

                  {combinedList.map((item) => {
                    const isExpanded = Boolean(expandedItems[item.primaryId]);
                    const panelId = `panel-${item.primaryId}-content`;
                    const headerId = `panel-${item.primaryId}-header`;

                    return (
                      <div key={item.primaryId} className="mb-4" id={`curriculum-item-${item.primaryId}`}>
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md" id={`accordion-${item.primaryId}`}>
                          <button
                            type="button"
                            aria-controls={panelId}
                            aria-expanded={isExpanded}
                            className="flex w-full items-center justify-between gap-3 p-4 text-left transition hover:bg-gray-50"
                            id={headerId}
                            onClick={() => toggleExpansion(item.primaryId)}
                          >
                            <span className="min-w-0 flex-grow">
                              <strong>{item.type === "C" ? `Chapter ${item.chapterIndex}` : "Exam"}:</strong> {item.name}
                            </span>
                            {item.type === "E" && <ClipboardCheck className="h-5 w-5 shrink-0" aria-hidden="true" />}
                            <ChevronDown
                              className={`h-5 w-5 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                              aria-hidden="true"
                            />
                          </button>
                          {isExpanded && (
                            <div
                              aria-labelledby={headerId}
                              className="border-t border-gray-100 p-4 text-sm text-gray-700"
                              id={panelId}
                              role="region"
                            >
                              {item.type === "C" && item.contentList?.length > 0 && (
                                <ul className="list-disc pl-5" id={`chapter-contents-${item.primaryId}`}>
                                  {item.contentList.map((content) => (
                                    <li key={content._id || content.primaryId} className="flex items-center gap-2">
                                      <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                      <span className="text-sm">{content.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {item.type === "E" && item.description && (
                                <RichTextDisplay content={item.description} sx={{ mt: 1 }} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        <span
          id="scroll-to-top"
          style={{ cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }}
          onClick={handleScrollToTop}
        >
          Scroll to Top
        </span>

        {alertMsg.open && (
          <div
            aria-live="polite"
            className="fixed left-1/2 top-5 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2"
            role="status"
          >
            <div className="relative" id="alert-message">
              <UiAlert tone={alertMsg.severity || "info"} className="pr-10 shadow-lg">
                {alertMsg.message}
              </UiAlert>
              <button
                type="button"
                aria-label="Close notification"
                className="absolute right-3 top-3 rounded p-1 text-current transition hover:bg-black/10"
                onClick={handleClose}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseInfo;
